/**
 * Session Manager
 * 
 * Handles session timeout, idle detection, and security controls.
 * Automatically logs out users when session expires or when security violations are detected.
 */

import { toast } from 'react-toastify';

class SessionManager {
  constructor() {
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds
    this.warningTime = 5 * 60 * 1000; // Show warning 5 minutes before expiry
    this.checkInterval = 60 * 1000; // Check every minute
    this.idleTimeout = 15 * 60 * 1000; // 15 minutes of inactivity
    
    this.sessionTimer = null;
    this.warningTimer = null;
    this.checkTimer = null;
    this.idleTimer = null;
    this.lastActivity = Date.now();
    this.warningShown = false;
    
    // Bind event handlers
    this.handleActivity = this.handleActivity.bind(this);
    this.checkSession = this.checkSession.bind(this);
    this.logout = this.logout.bind(this);
    
    this.initializeEventListeners();
  }

  /**
   * Initialize event listeners for activity detection
   */
  initializeEventListeners() {
    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, this.handleActivity, { passive: true });
    });

    // Handle visibility changes (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.handleActivity();
      }
    });

    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
  }

  /**
   * Handle user activity - reset timers
   */
  handleActivity() {
    this.lastActivity = Date.now();
    this.warningShown = false;
    
    // Update session last activity timestamp
    if (this.isUserLoggedIn()) {
      localStorage.setItem('lastActivity', this.lastActivity.toString());
      this.resetTimers();
    }
  }

  /**
   * Start session monitoring
   */
  startSession() {
    if (!this.isUserLoggedIn()) {
      console.log('SessionManager: No active session to monitor');
      return;
    }

    console.log('SessionManager: Starting session monitoring');
    this.lastActivity = Date.now();
    localStorage.setItem('sessionStart', this.lastActivity.toString());
    localStorage.setItem('lastActivity', this.lastActivity.toString());
    
    this.resetTimers();
    
    // Start periodic session checks
    this.checkTimer = setInterval(this.checkSession, this.checkInterval);
  }

  /**
   * Stop session monitoring
   */
  stopSession() {
    console.log('SessionManager: Stopping session monitoring');
    this.cleanup();
  }

  /**
   * Reset all timers
   */
  resetTimers() {
    // Clear existing timers
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.idleTimer) clearTimeout(this.idleTimer);

    // Set session timeout
    this.sessionTimer = setTimeout(() => {
      this.logout('Session expired due to timeout');
    }, this.sessionTimeout);

    // Set warning timer
    this.warningTimer = setTimeout(() => {
      this.showSessionWarning();
    }, this.sessionTimeout - this.warningTime);

    // Set idle timeout
    this.idleTimer = setTimeout(() => {
      this.logout('Session expired due to inactivity');
    }, this.idleTimeout);
  }

  /**
   * Show session expiry warning
   */
  showSessionWarning() {
    if (this.warningShown || !this.isUserLoggedIn()) return;
    
    this.warningShown = true;
    console.log('SessionManager: Showing session warning');
    
    toast.warning(
      'Your session will expire in 5 minutes. Please save your work and refresh the page to continue.',
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        onClose: () => { this.warningShown = false; }
      }
    );
  }

  /**
   * Check session validity
   */
  checkSession() {
    if (!this.isUserLoggedIn()) {
      this.stopSession();
      return;
    }

    const now = Date.now();
    const sessionStart = parseInt(localStorage.getItem('sessionStart') || '0');
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');

    // Check if session has expired
    if (now - sessionStart > this.sessionTimeout) {
      this.logout('Session expired due to timeout');
      return;
    }

    // Check if user has been idle too long
    if (now - lastActivity > this.idleTimeout) {
      this.logout('Session expired due to inactivity');
      return;
    }

    // Validate session integrity
    if (!this.validateSessionIntegrity()) {
      this.logout('Session integrity validation failed');
      return;
    }
  }

  /**
   * Validate session integrity
   */
  validateSessionIntegrity() {
    try {
      const userData = localStorage.getItem('userData');
      const token = localStorage.getItem('token');

      if (!userData || !token) {
        console.log('SessionManager: Missing authentication data');
        return false;
      }

      const user = JSON.parse(userData);
      if (!user.role || !user.email) {
        console.log('SessionManager: Invalid user data structure');
        return false;
      }

      // Check for suspicious role changes (basic check)
      const expectedRoles = ['student', 'admin', 'agent', 'sub-admin'];
      if (!expectedRoles.includes(user.role.toLowerCase())) {
        console.log('SessionManager: Invalid user role detected');
        return false;
      }

      return true;
    } catch (error) {
      console.error('SessionManager: Error validating session integrity:', error);
      return false;
    }
  }

  /**
   * Force logout with reason
   */
  logout(reason = 'Session ended') {
    console.log(`SessionManager: Logging out - ${reason}`);
    
    this.cleanup();
    
    // Clear ALL authentication data (including legacy format)
    localStorage.removeItem('userData');
    localStorage.removeItem('token');
    localStorage.removeItem('sessionStart');
    localStorage.removeItem('lastActivity');
    
    // Clear legacy format items that isUserAuthenticated() checks
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('lastSessionId');
    
    // Clear sessionStorage but set a flag to prevent redirect loop
    sessionStorage.clear();
    sessionStorage.setItem('sessionExpired', 'true');
    
    // Show logout message
    toast.error(reason + '. Please log in again.');
    
    // Use window.location.replace to prevent back button issues
    setTimeout(() => {
      window.location.replace('/login');
    }, 1000);
  }

  /**
   * Check if user is logged in
   */
  isUserLoggedIn() {
    return !!(localStorage.getItem('userData') && localStorage.getItem('token'));
  }

  /**
   * Get session info
   */
  getSessionInfo() {
    if (!this.isUserLoggedIn()) return null;

    const sessionStart = parseInt(localStorage.getItem('sessionStart') || '0');
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
    const now = Date.now();

    return {
      sessionDuration: now - sessionStart,
      idleDuration: now - lastActivity,
      timeUntilExpiry: this.sessionTimeout - (now - sessionStart),
      timeUntilIdleExpiry: this.idleTimeout - (now - lastActivity)
    };
  }

  /**
   * Extend session (refresh activity)
   */
  extendSession() {
    console.log('SessionManager: Extending session');
    this.handleActivity();
  }

  /**
   * Clean up timers and event listeners
   */
  cleanup() {
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.checkTimer) clearInterval(this.checkTimer);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    
    this.sessionTimer = null;
    this.warningTimer = null;
    this.checkTimer = null;
    this.idleTimer = null;
  }

  /**
   * Destroy session manager and clean up
   */
  destroy() {
    console.log('SessionManager: Destroying session manager');
    this.cleanup();
    
    // Remove event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
